import { streamingNotesPageSize } from '@/constants/streamingNotes'
import { baseApi } from '@/services'
import { PaginatedResults, StreamingNote, StreamingNotesQueryArgs } from '@/types/unlock'

export const streamingNoteApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @deprecated
     * @use useGetStreamingNotesByApplicationQuery
     * */
    listStreamingNotes: builder.query<PaginatedResults<StreamingNote[]>, any>({
      query: ({ application_id, only_parent, page, page_size }) => ({
        url: `v2/applications/${application_id}/streaming-notes/`,
        params: { only_parent, page, page_size },
      }),
      providesTags: ['StreamingNote'],
    }),

    getStreamingNotesByApplication: builder.query<
      PaginatedResults<StreamingNote[]>,
      StreamingNotesQueryArgs & { [key: string]: any }
    >({
      query: ({
        application_id,
        only_parent,
        page,
        page_size = streamingNotesPageSize,
        note_type,
        ...args
      }) => ({
        url: `v2/applications/${application_id}/streaming-notes/`,
        params: { only_parent, page, page_size, note_type, ...args },
      }),
      providesTags: ['StreamingNote'],
    }),

    getStreamingNote: builder.query<StreamingNote, string>({
      query: (uuid) => `v2/streaming-notes/${uuid}/`,
    }),

    createStreamingNote: builder.mutation<StreamingNote, any>({
      query: (body: StreamingNote) => ({
        method: 'POST',
        url: 'v2/streaming-notes/',
        body,
      }),
      invalidatesTags: ['StreamingNote', 'MaxActions'],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const resp = await queryFulfilled
          dispatch(
            streamingNoteApi.util.updateQueryData(
              'getStreamingNotesByApplication',
              { application_id: args.application, only_parent: true },
              (draft) => {
                if (!draft.count || draft.count === 0) draft.count = 1

                // Hack to get the document from the sessionStorage
                // This is due to backend not returning the
                // document object when creating the streaming note
                if (args.document) {
                  resp.data = {
                    ...resp.data,
                    document: JSON.parse(
                      sessionStorage.getItem('unlock:streamingNotes:userDocument')
                    ),
                  }
                }

                // Handle replies
                if (resp.data?.parent_streaming_note) {
                  const parent = draft.results.find(
                    (item) => item.id === resp.data.parent_streaming_note
                  )
                  if (parent) {
                    if (!parent.child_streaming_notes) parent.child_streaming_notes = []
                    parent.child_streaming_notes.push(resp.data)
                  }
                  return
                }
                draft.results.unshift(resp.data)
              }
            )
          )
        } catch (error) {
          console.error(error)
        }
      },
    }),

    updateStreamingNote: builder.mutation<StreamingNote, Partial<StreamingNote>>({
      query: (streamingNote) => ({
        method: 'PATCH',
        url: `v2/streaming-notes/${streamingNote.uuid}/`,
        body: streamingNote,
      }),
      invalidatesTags: ['StreamingNote'],
    }),

    deleteStreamingNote: builder.mutation<void, StreamingNote>({
      query: (streamingNote) => ({
        method: 'DELETE',
        url: `v2/streaming-notes/${streamingNote.uuid}/`,
      }),
      async onQueryStarted(streamingNote, { dispatch, queryFulfilled }) {
        // Optimistically remove the streaming note from the cache
        await queryFulfilled
        dispatch(
          streamingNoteApi.util.updateQueryData(
            'getStreamingNotesByApplication',
            { application_id: streamingNote.application, only_parent: true },
            (draft) => {
              if (streamingNote.parent_streaming_note) {
                const parent = draft.results.find(
                  (item) => item.id === streamingNote.parent_streaming_note
                )
                if (parent) {
                  parent.child_streaming_notes = parent.child_streaming_notes.filter(
                    (item) => item.uuid !== streamingNote.uuid
                  )
                }
                return
              }
              draft.results = draft.results.filter((item) => item.uuid !== streamingNote.uuid)
              if (draft.results.length === 0) draft.count = 0
            }
          )
        )
      },
    }),

    countStreamingNotes: builder.query<number, number>({
      query: (application_id) => ({
        method: 'GET',
        url: `v2/applications/${application_id}/streaming-notes/count`,
      }),
      providesTags: ['StreamingNote'],
    }),

    resetStreamingNotesCount: builder.mutation<void, number>({
      query: (application_id) => ({
        method: 'PATCH',
        url: `v2/applications/${application_id}/streaming-notes/reset-count/`,
      }),
      invalidatesTags: ['StreamingNote'],
    }),

    markStreamingNoteAsRead: builder.mutation<void, string>({
      query: (streamingNoteUUID) => ({
        method: 'POST',
        url: `v2/streaming-notes/${streamingNoteUUID}/mark-as-read/`,
      }),
    }),

    uamDecline: builder.mutation<void, any>({
      query: (body) => ({
        method: 'POST',
        url: `v2/streaming-notes/uam-decline/`,
        body,
      }),
      invalidatesTags: ['StreamingNote', 'Alert'],
    }),

    reactToStreamingNote: builder.mutation<void, any>({
      query: (body) => ({
        method: 'POST',
        url: `v2/streaming-note-reactions/`,
        body,
      }),
      invalidatesTags: ['StreamingNote'],
    }),

    deleteStreamingNoteReaction: builder.mutation<void, string>({
      query: (uuid) => ({
        method: 'DELETE',
        url: `v2/streaming-note-reactions/${uuid}/`,
      }),
      invalidatesTags: ['StreamingNote'],
    }),
  }),
  overrideExisting: true,
})

export const {
  useListStreamingNotesQuery,
  useGetStreamingNotesByApplicationQuery,
  useGetStreamingNoteQuery,
  useCreateStreamingNoteMutation,
  useUpdateStreamingNoteMutation,
  useDeleteStreamingNoteMutation,
  useCountStreamingNotesQuery,
  useResetStreamingNotesCountMutation,
  useMarkStreamingNoteAsReadMutation,
  useReactToStreamingNoteMutation,
  useDeleteStreamingNoteReactionMutation,
  useUamDeclineMutation,
} = streamingNoteApi
